import { useMemo } from 'react'
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type {
  UsageProviderSummary,
  UsageRecord,
  UsageTotals,
} from '../store/useObservabilityStore'

type FinancialSummaryCardProps = {
  usageTotals: UsageTotals
  usageByProvider: UsageProviderSummary[]
  usageByStep: Record<string, UsageRecord[]>
  runStatus?: string
  currency?: 'GBP' | 'USD'
  estimatedHumanLaborCost?: number
}

type SparkPoint = {
  index: number
  cost: number
}

function formatMoney(amount: number, currency: 'GBP' | 'USD') {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(amount)
}

function buildSparklineSeries(usageByStep: Record<string, UsageRecord[]>, fallbackCostMicros: number): SparkPoint[] {
  const all = Object.values(usageByStep)
    .flat()
    .filter((item) => Number(item.costMicros || 0) > 0)
    .sort((a, b) => {
      const left = a.occurredAt || ''
      const right = b.occurredAt || ''
      return left.localeCompare(right)
    })

  if (all.length === 0) {
    return [{ index: 0, cost: fallbackCostMicros / 1_000_000 }]
  }

  let running = 0
  return all.map((item, index) => {
    running += Number(item.costMicros || 0) / 1_000_000
    return {
      index,
      cost: running,
    }
  })
}

function providerBreakdown(usageByProvider: UsageProviderSummary[]) {
  const total = usageByProvider.reduce((sum, item) => sum + item.costMicros, 0)
  if (total <= 0 || usageByProvider.length === 0) {
    return 'No provider attribution yet'
  }
  return usageByProvider
    .filter((item) => item.costMicros > 0)
    .sort((a, b) => b.costMicros - a.costMicros)
    .map((item) => `${Math.round((item.costMicros / total) * 100)}% ${item.provider}`)
    .join(', ')
}

export function FinancialSummaryCard({
  usageTotals,
  usageByProvider,
  usageByStep,
  runStatus,
  currency = 'GBP',
  estimatedHumanLaborCost,
}: FinancialSummaryCardProps) {
  const totalCost = usageTotals.costMicros / 1_000_000
  const sparklineData = useMemo(
    () => buildSparklineSeries(usageByStep, usageTotals.costMicros),
    [usageByStep, usageTotals.costMicros],
  )

  const providerMix = useMemo(() => providerBreakdown(usageByProvider), [usageByProvider])
  const tokenEfficiency = useMemo(() => {
    const input = usageTotals.inputTokens || 0
    const output = usageTotals.outputTokens || 0
    if (input <= 0 && output <= 0) {
      return 'No token activity yet'
    }
    if (input <= 0) {
      return `0:${output.toFixed(0)}`
    }
    return `1:${(output / input).toFixed(2)}`
  }, [usageTotals.inputTokens, usageTotals.outputTokens])

  const humanCost = estimatedHumanLaborCost ?? Math.max(totalCost * 18, 12)
  const savings = Math.max(humanCost - totalCost, 0)
  const isRunning = runStatus === 'running'

  return (
    <section
      style={{
        background: 'linear-gradient(180deg, rgba(12,18,28,0.96) 0%, rgba(7,11,19,0.98) 100%)',
        border: `1px solid ${isRunning ? 'rgba(67, 160, 255, 0.42)' : 'rgba(120, 138, 160, 0.18)'}`,
        boxShadow: isRunning
          ? '0 0 0 1px rgba(67,160,255,0.18), 0 0 36px rgba(67,160,255,0.18)'
          : '0 18px 44px rgba(0,0,0,0.28)',
        borderRadius: 16,
        padding: 20,
        color: '#edf2f7',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 12,
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#93a4b8',
              marginBottom: 6,
            }}
          >
            Financial Summary
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>
            {formatMoney(totalCost, currency)}
          </div>
        </div>
        <div
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
            fontSize: 12,
            color: isRunning ? '#8ec8ff' : '#9fb0c6',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
          }}
        >
          {isRunning ? 'Live Run' : 'Closed Run'}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 12,
          marginBottom: 18,
        }}
      >
        <MetricBlock
          label="Token Efficiency"
          value={tokenEfficiency}
          detail={`${(usageTotals.inputTokens || 0).toFixed(0)} in / ${(usageTotals.outputTokens || 0).toFixed(0)} out`}
        />
        <MetricBlock label="Provider Breakdown" value={providerMix} />
        <MetricBlock
          label="Saved vs. Human Labor Cost"
          value={formatMoney(savings, currency)}
          detail={`Human baseline ${formatMoney(humanCost, currency)}`}
          tone="positive"
        />
        <MetricBlock
          label="Execution Footprint"
          value={`${Math.round(usageTotals.executionMs || 0).toString()} ms`}
          detail={`${(usageTotals.totalTokens || 0).toFixed(0)} total tokens`}
        />
      </div>

      <div
        style={{
          borderTop: '1px solid rgba(148, 163, 184, 0.12)',
          paddingTop: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <strong style={{ fontSize: 13, letterSpacing: '0.03em' }}>Cumulative Cost Curve</strong>
          <span
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
              fontSize: 12,
              color: '#9fb0c6',
            }}
          >
            {sparklineData.length} points
          </span>
        </div>
        <div style={{ width: '100%', height: 148 }}>
          <ResponsiveContainer>
            <AreaChart data={sparklineData} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="axiomCostFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5bc0ff" stopOpacity={0.38} />
                  <stop offset="100%" stopColor="#5bc0ff" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis dataKey="index" hide />
              <YAxis hide domain={['dataMin', 'dataMax']} />
              <Tooltip
                formatter={(value: number) => formatMoney(value, currency)}
                labelFormatter={(label) => `Point ${label}`}
                contentStyle={{
                  background: '#08101b',
                  border: '1px solid rgba(91,192,255,0.18)',
                  borderRadius: 10,
                  color: '#edf2f7',
                }}
              />
              <Area
                type="monotone"
                dataKey="cost"
                stroke={isRunning ? '#7ed0ff' : '#8bb9ff'}
                strokeWidth={2}
                fill="url(#axiomCostFill)"
                isAnimationActive={isRunning}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  )
}

function MetricBlock({
  label,
  value,
  detail,
  tone = 'default',
}: {
  label: string
  value: string
  detail?: string
  tone?: 'default' | 'positive'
}) {
  return (
    <div
      style={{
        border: '1px solid rgba(148, 163, 184, 0.12)',
        borderRadius: 12,
        padding: '12px 14px',
        background: 'rgba(255,255,255,0.02)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: '#8fa3bb',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
          fontSize: 16,
          fontWeight: 700,
          color: tone === 'positive' ? '#5ce0a0' : '#eef4ff',
          lineHeight: 1.3,
        }}
      >
        {value}
      </div>
      {detail ? (
        <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>
          {detail}
        </div>
      ) : null}
    </div>
  )
}
